import axios from "axios";
import { EventEmitter } from "events";
import { createContext, useContext, useState } from "react";

export const CityContext = createContext();

const CityEvents = new EventEmitter();

export const CityProvider = ({ children }) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });

  const [searchParams, setSearchParams] = useState({
    search: "",
  });

  // Update pagination state
  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  // Update search parameters
  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const createCity = async (cityData) => {
    try {
      const response = await axios.post(`/cities/`, cityData);
      const city = response.data;

      setCities((prevCities) => [city, ...prevCities]);

      // Émettre un événement pour notifier la mise à jour
      CityEvents.emit("city:created");

      return city;
    } catch (error) {
      console.error("Error creating city:", error);
      throw error;
    }
  };

  const getCities = async () => {
    try {
      setLoading(true);

      const params = {
        ...searchParams,
        page: pagination.page,
        limit: pagination.limit,
      };

      const response = await axios.get(`/cities`, { params });
      const { data, total, totalPages } = response.data;

      setCities(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      console.error("Error fetching cities:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getCityById = async (cityId) => {
    try {
      const response = await axios.get(`/cities/${cityId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching city by ID:", error);
      throw error;
    }
  };

  const updateCity = async (cityId, updatedData) => {
    try {
      const response = await axios.put(
        `/cities/${cityId}`,
        updatedData
      );
      const updatedCity = response.data;

      // Update the city in the context state
      setCities((prevCities) =>
        prevCities.map((city) =>
          city.id === updatedCity.id ? updatedCity : city
        )
      );

      // Émettre un événement pour notifier la mise à jour
      CityEvents.emit("city:updated");

      return updatedCity;
    } catch (error) {
      console.error("Error updating city:", error);
      throw error;
    }
  };

  const deleteCity = async (cityId) => {
    try {
      await axios.delete(`/cities/${cityId}`);

      // Supprime l'élément supprimé de la liste
      setCities((prevCities) =>
        prevCities.filter((city) => city.id !== cityId)
      );

      // Mettre à jour la pagination (diminuer total)
      setPagination((prev) => ({
        ...prev,
        total: prev.total > 0 ? prev.total - 1 : 0,
        totalPages: Math.ceil((prev.total - 1) / prev.limit),
      }));
    } catch (error) {
      console.error("Error deleting city:", error);
      throw error;
    }
  };

  // Context value
  const value = {
    cities,
    loading,
    pagination,
    searchParams,
    createCity,
    getCities,
    getCityById,
    updateCity,
    deleteCity,
    updatePagination,
    updateSearchParams,
    StaffEvents: CityEvents,
  };

  return <CityContext.Provider value={value}>{children}</CityContext.Provider>;
};

// Custom hook to use TeamContext
export const useCityContext = () => {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error("useCityContext must be used within a CityProvider");
  }
  return context;
};
